import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import UsersContent from './UsersContent';

const row = (overrides = {}) => ({
  id: 1,
  name: 'Abe Martinez',
  email: 'martinez@email.com',
  username: 'amartinez',
  jobRole: 'Fleet Manager',
  accounts: ['Account Name A'],
  permissions: ['Fleet Administrator'],
  permissionAssignments: [
    { account: 'Account Name A', permission: 'Fleet Administrator' },
  ],
  lastLoginAt: '03/08/2025 05:22 PM MST',
  ...overrides,
});

const PERMISSION_OPTIONS = ['Fleet Administrator', 'Manager', 'Member'];
const JOB_ROLE_OPTIONS = [
  { label: 'Fleet Manager', value: 'fleet_manager' },
  { label: 'Technician', value: 'technician' },
];
const ACCOUNT_OPTIONS = [
  { label: 'Account Name A', value: 1 },
  { label: 'Account Name B', value: 2 },
];

// What the wizard asks for. Only the shapes it reads: the wizard's own spec
// covers its behaviour, and this file only walks it far enough to create someone.
const WIZARD_OPTIONS = {
  jobRoles: JOB_ROLE_OPTIONS,
  countries: [{ label: 'United States', value: 'US' }],
  states: { US: [{ label: 'Virginia', value: 'VA' }] },
  phoneCountryCodes: [{ label: '+1', value: 1 }],
  permissions: [
    { id: 'fleet-administrator', label: 'Fleet Administrator', roleId: 'r1' },
  ],
};

// An account with no users yet, so the filter menu does not offer it: picking it
// could only empty the table. Assigning the first user is what makes it eligible,
// which is why a create can lengthen the menu.
const NEWLY_USED_ACCOUNT = { label: 'Fresh Fleet Account', value: 3 };

// What the wizard offers on step 1, which is scoped to accounts the viewer
// administers rather than to accounts that already have users.
const WIZARD_ACCOUNT = { label: 'Wizard Target Account', value: 9 };

// What the Remove / Restore Access modals load when they open (PD-4677).
const ACCOUNT_ACCESS = [
  { id: 1, name: 'Account Name A', removedAt: null },
  { id: 2, name: 'Account Name B', removedAt: '2025-01-01T00:00:00Z' },
];

// What the accounts endpoint currently answers. Mutable, because the point of the
// refresh test is that this changes underneath the page.
let accountOptions;

// The page and its wizard talk to several endpoints, so the mock dispatches on
// path rather than answering everything with the users payload.
const respondWith = (users, pagination = {}) => {
  accountOptions = ACCOUNT_OPTIONS;

  global.fetch = jest.fn().mockImplementation((url, options = {}) => {
    let body;

    if (options.method === 'POST') {
      // The created user's account starts qualifying, as it would server side.
      accountOptions = [...ACCOUNT_OPTIONS, NEWLY_USED_ACCOUNT];
      body = { user: { id: 42, username: 'jane.doe', name: 'Jane Doe' } };
    } else if (url.startsWith('/management/users/filter_options')) {
      body = { permissions: PERMISSION_OPTIONS, jobRoles: JOB_ROLE_OPTIONS };
    } else if (url.startsWith('/management/users/accounts')) {
      body = { accounts: accountOptions };
    } else if (url.startsWith('/management/users/admin_accounts')) {
      // Deliberately not one of ACCOUNT_OPTIONS: the wizard's list is a different
      // list, and a shared label would collide with the table's accounts cell.
      body = { accounts: [WIZARD_ACCOUNT], total: 1 };
    } else if (url.startsWith('/management/users/availability')) {
      body = { emailTaken: false, mobileTaken: false, mobileValid: true };
    } else if (url.startsWith('/management/users/authentication_options')) {
      // No domain rule, so the wizard's Authentication field is the admin's to
      // pick, and password login is on offer (PD-4684).
      body = {
        assignment: null,
        locked: false,
        provider: null,
        requireSso: false,
        options: [{ value: 'password', label: 'Password' }],
      };
    } else if (url.startsWith('/management/users/form_options')) {
      body = WIZARD_OPTIONS;
    } else if (url.includes('/account_access')) {
      body = { accountAccess: ACCOUNT_ACCESS };
    } else {
      body = {
        users,
        pagination: {
          page: 1,
          per_page: 25,
          total_entries: users.length,
          total_pages: 1,
          ...pagination,
        },
      };
    }

    return Promise.resolve({ ok: true, json: async () => body });
  });
};

const renderContent = (props = {}) =>
  render(
    <ConfigProvider>
      <UsersContent {...props} />
    </ConfigProvider>,
  );

const waitForRows = () =>
  waitFor(() => expect(screen.getByText('Abe Martinez')).toBeInTheDocument());

beforeEach(() => {
  window.history.replaceState({}, '', '/management/users');
});

afterEach(() => {
  delete global.fetch;
});

describe('management/Users UsersContent', () => {
  describe('the permissions cell', () => {
    it('shows the single role a user holds', async () => {
      respondWith([row({ permissions: ['Member'] })]);

      renderContent();
      await waitForRows();

      expect(screen.getByText('Member')).toBeInTheDocument();
      expect(screen.queryByText('Mixed Permissions')).not.toBeInTheDocument();
    });

    // Roles are per account, so someone working across accounts holds several.
    it('collapses several roles into Mixed Permissions', async () => {
      respondWith([row({ permissions: ['Manager', 'Member'] })]);

      renderContent();
      await waitForRows();

      expect(screen.getByText('Mixed Permissions')).toBeInTheDocument();
      expect(screen.queryByText('Manager')).not.toBeInTheDocument();
    });

    // Unlike ACCOUNTS, this cell shows no count — just the chevron.
    it('does not put a count beside Mixed Permissions', async () => {
      respondWith([row({ permissions: ['Manager', 'Member'] })]);

      renderContent();
      await waitForRows();

      expect(screen.queryByText('2')).not.toBeInTheDocument();
    });

    it('breaks the roles down by account when expanded', async () => {
      respondWith([
        row({
          permissions: ['Manager', 'Member'],
          permissionAssignments: [
            { account: 'Account Name B', permission: 'Manager' },
            { account: 'Account Name C', permission: 'Member' },
          ],
        }),
      ]);

      renderContent();
      await waitForRows();

      userEvent.click(
        screen.getByRole('button', { name: /show permissions by account/i }),
      );

      await waitFor(() =>
        expect(screen.getByText('Manager')).toBeInTheDocument(),
      );
      expect(screen.getByText('Account Name B')).toBeInTheDocument();
      expect(screen.getByText('Member')).toBeInTheDocument();
      expect(screen.getByText('Account Name C')).toBeInTheDocument();
    });

    it('renders a placeholder when the user holds no roles', async () => {
      respondWith([row({ permissions: [], permissionAssignments: [] })]);

      renderContent();
      await waitForRows();

      expect(screen.getAllByText('—').length).toBeGreaterThan(0);
    });
  });

  describe('the accounts cell', () => {
    it('shows the first account inline and the rest behind a count', async () => {
      respondWith([
        row({ accounts: ['Account Name A', 'Account Name B', 'Account C'] }),
      ]);

      renderContent();
      await waitForRows();

      expect(screen.getByText('Account Name A')).toBeInTheDocument();
      expect(
        screen.getByRole('button', { name: /show all accounts \(2 more\)/i }),
      ).toBeInTheDocument();
      expect(screen.queryByText('Account Name B')).not.toBeInTheDocument();
    });

    it('reveals the remaining accounts when expanded', async () => {
      respondWith([
        row({ accounts: ['Account Name A', 'Account Name B', 'Account C'] }),
      ]);

      renderContent();
      await waitForRows();

      const trigger = screen.getByRole('button', {
        name: /show all accounts/i,
      });
      expect(trigger).toHaveAttribute('aria-expanded', 'false');

      userEvent.click(trigger);

      await waitFor(() =>
        expect(screen.getByText('Account Name B')).toBeInTheDocument(),
      );
      expect(screen.getByText('Account C')).toBeInTheDocument();
      expect(trigger).toHaveAttribute('aria-expanded', 'true');
    });

    it('omits the overflow trigger for a single account', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      expect(
        screen.queryByRole('button', { name: /show all accounts/i }),
      ).not.toBeInTheDocument();
    });
  });

  describe('sorting', () => {
    it('refetches with the requested sort', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      userEvent.click(
        screen.getByRole('button', { name: /job role unsorted/i }),
      );

      await waitFor(() =>
        expect(global.fetch).toHaveBeenCalledWith(
          '/management/users.json?page=1&sort_by=job_role&sort_order=ASC',
          expect.anything(),
        ),
      );
    });
  });

  describe('pagination', () => {
    it('reports the range and total', async () => {
      respondWith([row()], { total_entries: 3, total_pages: 1, per_page: 25 });

      renderContent();
      await waitForRows();

      expect(screen.getByText(/1 - 3 of 3 Users/)).toBeInTheDocument();
    });

    it('refetches when another page is chosen', async () => {
      respondWith([row()], { total_entries: 60, total_pages: 3 });

      renderContent();
      await waitForRows();

      userEvent.click(screen.getByRole('button', { name: 'Go to page 2' }));

      await waitFor(() =>
        expect(global.fetch).toHaveBeenCalledWith(
          '/management/users.json?page=2',
          expect.anything(),
        ),
      );
    });
  });

  describe('search', () => {
    // The Figma component is "Search on Submit", so typing alone must not fetch.
    it('does not search until Enter is pressed', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      global.fetch.mockClear();
      fireEvent.change(screen.getByRole('textbox', { name: /search users/i }), {
        target: { value: 'martinez' },
      });

      expect(global.fetch).not.toHaveBeenCalled();
    });

    it('searches on Enter', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      const input = screen.getByRole('textbox', { name: /search users/i });
      fireEvent.change(input, { target: { value: 'martinez' } });
      fireEvent.keyPress(input, { key: 'Enter', charCode: 13 });

      await waitFor(() =>
        expect(global.fetch).toHaveBeenCalledWith(
          '/management/users.json?page=1&q=martinez',
          expect.anything(),
        ),
      );
    });

    // Emptying the box should restore the list without a second Enter.
    it('searches again as soon as the box is cleared', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      const input = screen.getByRole('textbox', { name: /search users/i });
      fireEvent.change(input, { target: { value: 'martinez' } });
      fireEvent.keyPress(input, { key: 'Enter', charCode: 13 });

      await waitFor(() =>
        expect(window.location.search).toEqual('?q=martinez'),
      );

      fireEvent.change(input, { target: { value: '' } });

      await waitFor(() => expect(window.location.search).toEqual(''));
    });

    it('tells the user what the search covers', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      expect(
        screen.getByText('Search by name, username, or email.'),
      ).toBeInTheDocument();
    });
  });

  describe('when nothing matches', () => {
    const searchFor = async (term) => {
      const input = screen.getByRole('textbox', { name: /search users/i });
      fireEvent.change(input, { target: { value: term } });
      fireEvent.keyPress(input, { key: 'Enter', charCode: 13 });
    };

    // Figma 787:78141 keeps the headers and the footer here, unlike the
    // genuinely-empty state, so the user can undo what they narrowed.
    it('keeps the column headers and the footer', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      respondWith([], { total_entries: 0, total_pages: 0 });
      await searchFor('nobody');

      await waitFor(() =>
        expect(screen.getByText(/No Matches for/)).toBeInTheDocument(),
      );

      expect(screen.queryAllByRole('columnheader').length).toBeGreaterThan(0);
      expect(screen.getByText(/of 0 Users/)).toBeInTheDocument();
    });

    it('quotes the search term back in the title', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      respondWith([], { total_entries: 0, total_pages: 0 });
      await searchFor('Abe_Martinez');

      await waitFor(() =>
        expect(
          screen.getByText('No Matches for \u201cAbe_Martinez\u201d'),
        ).toBeInTheDocument(),
      );
    });

    it('is shown instead of the no-users-at-all state', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      respondWith([], { total_entries: 0, total_pages: 0 });
      await searchFor('nobody');

      await waitFor(() =>
        expect(screen.getByText(/No Matches for/)).toBeInTheDocument(),
      );

      expect(screen.queryByText('No Users Yet')).not.toBeInTheDocument();
    });
  });

  // So the view survives a reload and can be linked to.
  describe('address bar', () => {
    it('leaves the URL bare on the first page, unsorted', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      expect(window.location.search).toEqual('');
    });

    it('records the page once the user moves off the first', async () => {
      respondWith([row()], { total_entries: 60, total_pages: 3 });

      renderContent();
      await waitForRows();

      userEvent.click(screen.getByRole('button', { name: 'Go to page 2' }));

      await waitFor(() => expect(window.location.search).toEqual('?page=2'));
    });

    it('records the sort', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      userEvent.click(
        screen.getByRole('button', { name: /job role unsorted/i }),
      );

      await waitFor(() =>
        expect(window.location.search).toEqual(
          '?sort_by=job_role&sort_order=ASC',
        ),
      );
    });

    it('reads the page and sort back on mount, as a reload would', async () => {
      window.history.replaceState(
        {},
        '',
        '/management/users?page=3&sort_by=last_login_at&sort_order=DESC',
      );
      respondWith([row()], { page: 3, total_entries: 90, total_pages: 4 });

      renderContent();
      await waitForRows();

      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users.json?page=3&sort_by=last_login_at&sort_order=DESC',
        expect.anything(),
      );
    });
  });

  describe('the Create User wizard', () => {
    // The wizard mounts with the page but stays hidden, so its option lists cost
    // nothing until somebody opens it.
    it('stays closed until the button asks for it', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      expect(
        screen.queryByText('Select the accounts to assign this user to.'),
      ).not.toBeInTheDocument();
      expect(global.fetch).not.toHaveBeenCalledWith(
        expect.stringContaining('form_options'),
        expect.anything(),
      );
    });

    it('opens from the CREATE USER menu', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      fireEvent.click(screen.getByRole('button', { name: /create user/i }));
      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );
      fireEvent.click(screen.getByRole('option', { name: 'Create User' }));

      await waitFor(() =>
        expect(
          screen.getByText('Select the accounts to assign this user to.'),
        ).toBeInTheDocument(),
      );
    });

    /** Opens the wizard and walks it to a created user, then closes it. */
    const createAUser = async () => {
      fireEvent.click(screen.getByRole('button', { name: /create user/i }));
      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );
      fireEvent.click(screen.getByRole('option', { name: 'Create User' }));

      // Step 1: an account, from the wizard's own admin_accounts list. Reached by
      // its label rather than as a button, because the field's trigger and the
      // page's "All Accounts" filter would both answer to /account/i.
      await waitFor(() =>
        expect(screen.getByLabelText('Account')).toBeInTheDocument(),
      );
      fireEvent.focus(screen.getByLabelText('Account'));
      await waitFor(() =>
        expect(
          screen.getByRole('button', { name: /^apply$/i }),
        ).toBeInTheDocument(),
      );
      fireEvent.click(screen.getByText(WIZARD_ACCOUNT.label));
      fireEvent.click(screen.getByRole('button', { name: /^apply$/i }));

      const next = () => screen.getByRole('button', { name: /^continue$/i });
      await waitFor(() => expect(next()).toBeEnabled());
      fireEvent.click(next());

      // Step 2: the required identity fields.
      fireEvent.change(screen.getByLabelText('First Name'), {
        target: { value: 'Jane' },
      });
      fireEvent.change(screen.getByLabelText('Last Name'), {
        target: { value: 'Doe' },
      });
      fireEvent.change(screen.getByLabelText('Email'), {
        target: { value: 'jane.doe@example.com' },
      });

      // Authentication is required too (PD-4684). Its options are fetched, so the
      // field is inert until they land; the help line appearing is what says they
      // have. A KeyDS Select carries no HTML `disabled` attribute to wait on.
      await waitFor(() =>
        expect(
          screen.getByText('This will determine how the user accesses the app.'),
        ).toBeInTheDocument(),
      );
      fireEvent.click(screen.getByLabelText('Authentication'));
      fireEvent.click(screen.getByText('Password'));

      await waitFor(() => expect(next()).toBeEnabled());
      fireEvent.click(next());

      // Step 3: one permission, then create.
      await waitFor(() =>
        expect(
          screen.getByLabelText('Fleet Administrator'),
        ).toBeInTheDocument(),
      );
      fireEvent.click(screen.getByLabelText('Fleet Administrator'));

      // Two buttons answer to this name: the page's CREATE USER split button and
      // the wizard's own submit. Only the split button opens a menu, so that is
      // what tells them apart.
      const create = () =>
        screen
          .getAllByRole('button', { name: /^create user$/i })
          .find((element) => !element.hasAttribute('aria-haspopup'));

      await waitFor(() => expect(create()).toBeEnabled());
      fireEvent.click(create());

      await waitFor(() =>
        expect(
          screen.getByRole('button', { name: /^close$/i }),
        ).toBeInTheDocument(),
      );
      fireEvent.click(screen.getByRole('button', { name: /^close$/i }));
    };

    // The reported gap: the accounts filter offers only accounts that already have
    // a listable user, so the account a brand new user was just assigned to was
    // missing from the very menu you would filter by to find them.
    it('picks up an account that the new user made eligible', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      // Opened and closed from its own trigger. Waited on by the menu's Apply
      // rather than by an account label, since those also appear in the table's
      // accounts cell.
      const accountsFilter = () =>
        screen.getByRole('button', { name: /all accounts/i });

      fireEvent.click(accountsFilter());
      await waitFor(() =>
        expect(
          screen.getByRole('button', { name: /^apply$/i }),
        ).toBeInTheDocument(),
      );
      expect(
        screen.queryByText(NEWLY_USED_ACCOUNT.label),
      ).not.toBeInTheDocument();

      fireEvent.click(accountsFilter());
      await waitFor(() =>
        expect(
          screen.queryByRole('button', { name: /^apply$/i }),
        ).not.toBeInTheDocument(),
      );

      await createAUser();

      fireEvent.click(accountsFilter());
      await waitFor(() =>
        expect(screen.getByText(NEWLY_USED_ACCOUNT.label)).toBeInTheDocument(),
      );
    });
  });

  // An admin is told an import finished exactly once: on the wizard's summary
  // step if they stayed with it, as a toast if they closed it. QA found both at
  // the same time (PD-4066), because this page collected the event regardless of
  // whether the wizard was on screen reporting the same thing.
  describe('the bulk import event poll', () => {
    const eventPolls = () =>
      global.fetch.mock.calls.filter(([url]) =>
        url.startsWith('/management/user_events'),
      ).length;

    const openBulkWizard = async () => {
      fireEvent.click(screen.getByRole('button', { name: /create user/i }));
      await waitFor(() =>
        expect(
          screen.getByRole('option', { name: 'Create Multiple Users' }),
        ).toBeInTheDocument(),
      );
      fireEvent.click(
        screen.getByRole('option', { name: 'Create Multiple Users' }),
      );
      await waitFor(() =>
        expect(screen.getByText('UPLOAD CSV FILE')).toBeInTheDocument(),
      );
    };

    it('pauses while the bulk wizard is open and resumes when it closes', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();
      // The hook collects once on mount, so an import that finished while the
      // admin was elsewhere surfaces on arrival.
      await waitFor(() => expect(eventPolls()).toBeGreaterThan(0));

      const beforeOpening = eventPolls();
      await openBulkWizard();
      expect(eventPolls()).toEqual(beforeOpening);

      fireEvent.click(screen.getByRole('button', { name: /^cancel$/i }));

      // Re-enabling collects immediately rather than after the 5s interval, so a
      // different import that finished while the wizard was open is not sat on.
      await waitFor(() => expect(eventPolls()).toBeGreaterThan(beforeOpening));
    });
  });

  // PD-4677: the Current Access / Removed Access split, the row ACTIONS menu and
  // the modals behind it.
  describe('the access tabs', () => {
    const usersRequests = () =>
      global.fetch.mock.calls
        .map(([url]) => url)
        .filter((url) => url.startsWith('/management/users.json'));

    it('offers both tabs', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      expect(
        screen.getByRole('tab', { name: 'Current Access' }),
      ).toBeInTheDocument();
      expect(
        screen.getByRole('tab', { name: 'Removed Access' }),
      ).toBeInTheDocument();
    });

    // Current is the server's default too, so spelling it would put a redundant
    // param on every request the page makes.
    it('asks for no access param on the current tab', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      expect(usersRequests()[0]).not.toContain('access=');
    });

    it('asks for the removed cohort on the other tab', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      fireEvent.click(screen.getByRole('tab', { name: 'Removed Access' }));

      await waitFor(() =>
        expect(
          usersRequests().some((url) => url.includes('access=removed')),
        ).toBe(true),
      );
    });

    it('writes the tab to the address bar so a reload lands on it', async () => {
      respondWith([row()]);

      renderContent();
      await waitForRows();

      fireEvent.click(screen.getByRole('tab', { name: 'Removed Access' }));

      await waitFor(() =>
        expect(window.location.search).toContain('access=removed'),
      );
    });

    it('reads the tab back on mount, as a reload would', async () => {
      window.history.replaceState({}, '', '/management/users?access=removed');
      respondWith([row()]);

      renderContent();
      await waitForRows();

      expect(usersRequests()[0]).toContain('access=removed');
    });

    // Page 3 of one cohort rarely exists in the other.
    it('returns to the first page when the tab changes', async () => {
      window.history.replaceState({}, '', '/management/users?page=3');
      respondWith([row()]);

      renderContent();
      await waitForRows();

      fireEvent.click(screen.getByRole('tab', { name: 'Removed Access' }));

      await waitFor(() => {
        const last = usersRequests()[usersRequests().length - 1];
        expect(last).toContain('page=1');
      });
    });

    it('shows when access was removed, which the current tab has no column for', async () => {
      window.history.replaceState({}, '', '/management/users?access=removed');
      respondWith([row({ removedAt: '04/01/2025 09:00 AM MST' })]);

      renderContent();
      await waitForRows();

      expect(screen.getByText('04/01/2025 09:00 AM MST')).toBeInTheDocument();
    });
  });

  describe('the row actions menu', () => {
    it('is absent for a viewer who may only look', async () => {
      respondWith([row()]);

      renderContent({ canEdit: false });
      await waitForRows();

      expect(
        screen.queryByRole('button', { name: /Actions for/ }),
      ).not.toBeInTheDocument();
    });

    it('is offered to a viewer who may edit', async () => {
      respondWith([row()]);

      renderContent({ canEdit: true });
      await waitForRows();

      expect(
        screen.getByRole('button', { name: 'Actions for Abe Martinez' }),
      ).toBeInTheDocument();
    });

    it('opens the Remove Access modal on the current tab', async () => {
      respondWith([row()]);

      renderContent({ canEdit: true });
      await waitForRows();

      fireEvent.click(
        screen.getByRole('button', { name: 'Actions for Abe Martinez' }),
      );
      fireEvent.click(screen.getByRole('menuitem', { name: /Remove Access/ }));

      expect(
        await screen.findByText(
          'Clicking REMOVE ACCESS will prevent this user from accessing the selected accounts.',
        ),
      ).toBeInTheDocument();
    });

    it('opens the Restore Access modal on the removed tab', async () => {
      window.history.replaceState({}, '', '/management/users?access=removed');
      respondWith([row()]);

      renderContent({ canEdit: true });
      await waitForRows();

      fireEvent.click(
        screen.getByRole('button', { name: 'Actions for Abe Martinez' }),
      );
      fireEvent.click(screen.getByRole('menuitem', { name: /Restore Access/ }));

      expect(
        await screen.findByText(
          'Clicking RESTORE ACCESS will again grant this user access to the selected accounts with previous permissions.',
        ),
      ).toBeInTheDocument();
    });
  });
});
